import { useState, useRef, useEffect } from 'react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import RecordChip from './RecordChip';
import AssetCardModal from './AssetCardModal';

const SESSION_TTL = 4 * 60 * 60 * 1000; // 4 hours

const WELCOME = {
  role:    'assistant',
  answer:  "Hey — I'm Clio, your production assistant. Ask me anything about what's happening in the pipeline. Try something like 'Which scripts are stuck in qual?' or 'Show me all Dental Digest videos in editing.'",
  records: [],
};

const HELP_WELCOME = {
  role:    'assistant',
  answer:  "Hey — I'm Clio. Ask me anything about how Heliconflow works — features, workflows, permissions, or what any button does. You can also ask about your pipeline data anytime.",
  records: [],
};

function loadSession(key) {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const { messages, lastAt } = JSON.parse(raw);
    if (Date.now() - lastAt > SESSION_TTL) {
      sessionStorage.removeItem(key);
      return null;
    }
    return messages;
  } catch {
    return null;
  }
}

function saveSession(key, messages) {
  try {
    sessionStorage.setItem(key, JSON.stringify({ messages, lastAt: Date.now() }));
  } catch {}
}

const SUGGESTIONS = [
  "Which scripts have been in qual for more than 5 days?",
  "Show me all approved videos with no posting slot yet",
  "Give me a pipeline health check — anything that looks off?",
];

const HELP_SUGGESTIONS = [
  "How do I send a script to production?",
  "What does each Live production status mean?",
  "How do I run a billing report for a project?",
  "What can a Producer do in Heliconflow?",
];

function TypingDots() {
  return (
    <div className="typing-dots">
      <div className="typing-dot" />
      <div className="typing-dot" />
      <div className="typing-dot" />
    </div>
  );
}

function ChipRow({ records }) {
  const [expanded, setExpanded] = useState(false);
  if (!records?.length) return null;
  const visible = expanded ? records : records.slice(0, 6);
  const hidden  = records.length - 6;
  return (
    <div className="record-chips-row">
      {visible.map((r, i) => (
        <RecordChip key={r.id + i} id={r.id} type={r.type} name={r.name} detail={r.detail} />
      ))}
      {!expanded && hidden > 0 && (
        <button className="chips-show-more" onClick={() => setExpanded(true)}>
          +{hidden} more
        </button>
      )}
    </div>
  );
}

function AssetResultGrid({ assets, onCardClick }) {
  if (!assets?.length) return null;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8, marginTop: 10 }}>
      {assets.map(a => {
        const tags = a.aiTags || {};
        const topTags = [
          ...(tags.objects || []).slice(0, 1),
          ...(tags.brands  || []).slice(0, 1),
          ...(tags.themes  || []).slice(0, 1),
        ].filter(Boolean);
        return (
          <button
            key={a.id}
            onClick={() => onCardClick(a.id)}
            style={{
              background: 'var(--bg-card)', border: '1px solid var(--border)',
              borderRadius: 'var(--radius-md)', padding: 0, cursor: 'pointer',
              textAlign: 'left', overflow: 'hidden',
              display: 'flex', flexDirection: 'column',
            }}
          >
            <div style={{ width: '100%', aspectRatio: '4/3', background: 'var(--bg-input)', overflow: 'hidden', flexShrink: 0 }}>
              {a.thumbnailUrl
                ? <img src={a.thumbnailUrl} alt={a.fileName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, color: 'var(--tx-faint)' }}>▣</div>
              }
            </div>
            <div style={{ padding: '7px 8px', display: 'flex', flexDirection: 'column', gap: 5 }}>
              {a.aiDescription && (
                <div style={{
                  fontSize: 10, color: 'var(--tx-secondary)', lineHeight: 1.45,
                  display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                }}>
                  {a.aiDescription}
                </div>
              )}
              {topTags.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  {topTags.map((t, i) => (
                    <span key={i} style={{
                      fontSize: 9, padding: '2px 6px', borderRadius: 99,
                      background: 'rgba(139,109,255,0.12)', color: 'var(--violet)', fontWeight: 600,
                    }}>{t}</span>
                  ))}
                </div>
              )}
            </div>
          </button>
        );
      })}
    </div>
  );
}

function AssistantBubble({ message, onAssetClick }) {
  return (
    <div className="chat-bubble-assistant">
      <div className="chat-bubble-assistant-inner">
        {message.loading ? (
          <TypingDots />
        ) : (
          <>
            <div style={{ color: 'var(--tx-primary)', lineHeight: 1.6 }}>{message.answer}</div>
            <ChipRow records={message.records} />
            <AssetResultGrid assets={message.assets} onCardClick={onAssetClick} />
            {message.doc && (
              <a
                href={message.doc.url}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 10,
                  fontSize: 12.5, fontWeight: 600, color: 'var(--violet)', textDecoration: 'none',
                  border: '1px solid var(--violet-glow)', background: 'var(--violet-soft)',
                  borderRadius: 'var(--radius-pill, 100px)', padding: '4px 12px',
                }}
              >
                📖 Full details: {message.doc.label} docs ↗
              </a>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// mode: 'page' | 'drawer'
// sessionKey: storage key so page and drawer histories are independent
export default function ClioChat({ mode = 'page', initialHelpMode = false, sessionKey = 'helicon_chat' }) {
  const { user } = useAuth();
  const isHelpMode = initialHelpMode;

  const restored = isHelpMode ? null : loadSession(sessionKey);
  const [messages,    setMessages]    = useState(restored || (isHelpMode ? [HELP_WELCOME] : [WELCOME]));
  const [input,       setInput]       = useState('');
  const [loading,     setLoading]     = useState(false);
  const [firstSent,   setFirstSent]   = useState(restored ? restored.length > 1 : false);
  const [openAssetId, setOpenAssetId] = useState(null);
  const threadRef = useRef(null);
  const inputRef  = useRef(null);

  const suggestions = isHelpMode ? HELP_SUGGESTIONS : SUGGESTIONS;

  useEffect(() => {
    if (threadRef.current) {
      threadRef.current.scrollTop = threadRef.current.scrollHeight;
    }
    saveSession(sessionKey, messages);
  }, [messages, sessionKey]);

  const submit = async (q) => {
    const question = q.trim();
    if (!question || loading) return;

    setFirstSent(true);
    setInput('');
    setMessages(prev => [
      ...prev,
      { role: 'user', question },
      { role: 'assistant', loading: true },
    ]);
    setLoading(true);

    const history = messages
      .filter(m => !m.loading && (m.role === 'user' ? !!m.question : !!m.answer))
      .slice(-6)
      .map(m => m.role === 'user'
        ? { role: 'user', content: m.question }
        : { role: 'assistant', content: m.queryDescription
            ? `[Query context: ${m.queryDescription}]\n${m.answer}`
            : m.answer }
      );

    try {
      const res = await api.post('/api/assistant/query', { question, history });
      setMessages(prev => {
        const next = [...prev];
        next[next.length - 1] = {
          role:             'assistant',
          answer:           res.data.answer,
          records:          res.data.records || [],
          assets:           res.data.assets  || [],
          doc:              res.data.doc     || null,
          queryDescription: res.data.queryDescription || '',
        };
        return next;
      });
    } catch {
      setMessages(prev => {
        const next = [...prev];
        next[next.length - 1] = {
          role:    'assistant',
          answer:  'Something went wrong on my end. Check your connection and try again.',
          records: [],
        };
        return next;
      });
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit(input);
    }
  };

  const assetModal = openAssetId && (
    <AssetCardModal
      assetId={openAssetId}
      user={user}
      folders={[]}
      onClose={() => setOpenAssetId(null)}
    />
  );

  // ── Drawer mode ──────────────────────────────────────────────────────────────
  if (mode === 'drawer') {
    return (
      <>
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '12px 14px', display: 'flex', flexDirection: 'column' }}>
          {messages.map((msg, i) =>
            msg.role === 'user' ? (
              <div key={i} className="chat-bubble-user">
                <div className="chat-bubble-user-inner">{msg.question}</div>
              </div>
            ) : (
              <AssistantBubble key={i} message={msg} onAssetClick={id => setOpenAssetId(id)} />
            )
          )}
          <div ref={threadRef} />
        </div>

        <div style={{ borderTop: '1px solid var(--border)', padding: '10px 12px', flexShrink: 0 }}>
          {!firstSent && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginBottom: 8 }}>
              {suggestions.map((s, i) => (
                <button key={i} className="chat-suggestion-chip" onClick={() => submit(s)}
                  style={{ fontSize: 11, textAlign: 'left' }}>
                  {s}
                </button>
              ))}
            </div>
          )}
          <div className="chat-input-row">
            <textarea
              ref={inputRef}
              className="chat-input"
              placeholder="Ask Clio anything…"
              value={input}
              rows={1}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={loading}
            />
            <button
              className="chat-send-btn"
              onClick={() => submit(input)}
              disabled={loading || !input.trim()}
            >
              Send
            </button>
          </div>
        </div>
        {assetModal}
      </>
    );
  }

  // ── Page mode (default) ──────────────────────────────────────────────────────
  return (
    <div className="main" style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <div className="topbar">
        <div>
          <div className="page-title">Assistant</div>
          <div className="page-sub">{isHelpMode ? 'Clio — ask anything about how Heliconflow works' : 'Clio — ask anything about your pipeline'}</div>
        </div>
      </div>

      <div ref={threadRef} className="chat-thread">
        <div className="chat-center">
          {messages.map((msg, i) =>
            msg.role === 'user' ? (
              <div key={i} className="chat-bubble-user">
                <div className="chat-bubble-user-inner">{msg.question}</div>
              </div>
            ) : (
              <AssistantBubble key={i} message={msg} onAssetClick={id => setOpenAssetId(id)} />
            )
          )}
        </div>
      </div>

      <div className="chat-input-bar">
        <div className="chat-input-inner">
          {!firstSent && (
            <div className="chat-suggestions">
              {suggestions.map((s, i) => (
                <button key={i} className="chat-suggestion-chip" onClick={() => submit(s)}>{s}</button>
              ))}
            </div>
          )}
          <div className="chat-input-row">
            <textarea
              ref={inputRef}
              className="chat-input"
              placeholder="Ask Clio anything about production…"
              value={input}
              rows={1}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={loading}
            />
            <button
              className="chat-send-btn"
              onClick={() => submit(input)}
              disabled={loading || !input.trim()}
            >
              Send
            </button>
          </div>
        </div>
      </div>
      {assetModal}
    </div>
  );
}
